import { Component, OnInit } from "@angular/core";
import { NavigationCancel, NavigationEnd, NavigationError, Router } from "@angular/router";
import { filter, firstValueFrom, take } from "rxjs";
import { Platform } from "@ionic/angular";
import { SplashScreen } from "@ionic-native/splash-screen/ngx";
import { StatusBar } from "@ionic-native/status-bar/ngx";
import { addIcons } from "ionicons";
import {
  addCircle,
  addCircleOutline,
  alertCircleOutline,
  archiveOutline,
  arrowBackCircleOutline,
  arrowRedoCircleOutline,
  call,
  callOutline,
  cameraOutline,
  car,
  carOutline,
  cardOutline,
  cashOutline,
  checkmarkCircleOutline,
  checkmarkOutline,
  close,
  closeCircleOutline,
  codeOutline,
  cube,
  documentTextOutline,
  downloadOutline,
  exitOutline,
  helpCircleOutline,
  home,
  homeOutline,
  informationCircleOutline,
  informationOutline,
  list,
  lockClosedOutline,
  logOutOutline,
  mailOutline,
  notifications,
  notificationsOutline,
  person,
  playCircleOutline,
  printOutline,
  qrCodeOutline,
  receiptOutline,
  removeCircle,
  reorderFourOutline,
  ribbonOutline,
  scanOutline,
  settingsOutline,
  shareOutline,
  star,
  timeOutline,
  trashOutline,
  warningOutline,
} from "ionicons/icons";
import { AuthService } from "src/services/auth/auth.service";
import { User, userToString } from "src/services/user/user.interface";
import { environment } from "@environments/environment";
import { Role } from "src/services/user/role.enum";
import { logSentry } from "src/services/utils/sentry.utils";
import { referralLocalStorageKey, referralQueryParamKey } from "src/services/referral/referral.service";
import { ReferralApiService } from "src/services/referral/referral-api.service";
import { adminPages, manageNotificationsPage } from "src/services/navigation/paths.const";
import { tryParseDate } from "src/services/utils/timestamp.utils";
import { NotificationService } from "src/services/notifications/notifications.service";
import { MessageService } from "../services/message/message.service";
import { NavigationService } from "../services/navigation/navigation.service";
import { ApiService } from "../services/http/api.service";
import { apiPath } from "../shared/api-path.const";
import { HttpService } from "../services/http/http.service";
import { TracingRequestBody } from "../shared/local-api.types";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  standalone: false,
})
export class AppComponent implements OnInit {
  public appPages: { title: string; children: { title: string; url: string }[] }[] = [];
  public selectedIndex = 0;
  public appVersion = "";
  public booting = true;

  private readonly bootOverlayMaxDurationMs = 45000;

  public activeUser: User | undefined;
  public referralPromoCode: string | null = null;
  public showDeleteAccountRequest = false;

  private versionLogged: boolean = false;
  private isCheckingMenu: boolean = false;
  private lastCheckedUserId: string | undefined;

  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService,
    private readonly httpService: HttpService,
    private readonly messageService: MessageService,
    private readonly navigationService: NavigationService,
    private readonly notificationService: NotificationService,
    private readonly platform: Platform,
    private readonly referralService: ReferralApiService,
    private readonly router: Router,
    private readonly splashScreen: SplashScreen,
    private readonly statusBar: StatusBar,
  ) {
    addIcons({
      addCircle,
      addCircleOutline,
      alertCircleOutline,
      archiveOutline,
      arrowBackCircleOutline,
      arrowRedoCircleOutline,
      call,
      callOutline,
      cameraOutline,
      car,
      carOutline,
      cardOutline,
      cashOutline,
      checkmarkCircleOutline,
      checkmarkOutline,
      close,
      closeCircleOutline,
      codeOutline,
      cube,
      documentTextOutline,
      downloadOutline,
      exitOutline,
      helpCircleOutline,
      home,
      homeOutline,
      informationCircleOutline,
      informationOutline,
      list,
      lockClosedOutline,
      logOutOutline,
      mailOutline,
      notifications,
      notificationsOutline,
      person,
      playCircleOutline,
      printOutline,
      qrCodeOutline,
      receiptOutline,
      removeCircle,
      reorderFourOutline,
      ribbonOutline,
      scanOutline,
      settingsOutline,
      shareOutline,
      star,
      timeOutline,
      trashOutline,
      warningOutline,
    });
    this.initializeApp();
    this.authService.getActiveUserObservable().subscribe(() => {
      void this.checkMenu();
    });
  }

  public initializeApp(): void {
    this.platform.ready().then(async () => {
      this.statusBar.styleDefault();
      this.splashScreen.hide();
      await this.notificationService.listenForNotifications();
    });
  }

  public async ngOnInit(): Promise<void> {
    this.appVersion = environment.appVersion;
    await this.storeReferralCoupon();
    await this.hideBootOverlayWhenReady();
  }

  private async hideBootOverlayWhenReady(): Promise<void> {
    const firstNavigation = this.router.navigated
      ? Promise.resolve()
      : firstValueFrom(
          this.router.events.pipe(
            filter(
              (event) =>
                event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError,
            ),
            take(1),
          ),
        );

    const appReady = Promise.all([this.authService.waitForAuthInit(), firstNavigation]);

    let capTimer: ReturnType<typeof setTimeout> | undefined;
    const maxDuration = new Promise<void>((resolve) => {
      capTimer = setTimeout(resolve, this.bootOverlayMaxDurationMs);
    });

    try {
      await Promise.race([appReady, maxDuration]);
    } finally {
      clearTimeout(capTimer);
      this.booting = false;
    }
  }

  private async checkMenu(): Promise<void> {
    if (this.isCheckingMenu) {
      return;
    }

    this.isCheckingMenu = true;

    try {
      const activeUser = await this.authService.getActiveUser();
      this.activeUser = activeUser;
      if (!activeUser) {
        return;
      }

      const userId = activeUser.id;

      if (this.lastCheckedUserId === userId) {
        return;
      }

      this.lastCheckedUserId = userId;

      if (!this.versionLogged) {
        this.versionLogged = true;
        void this.httpService.sendPostRequest<TracingRequestBody, unknown>(environment.endpoints.traceUserVersion, {
          userId,
          version: this.appVersion,
        });
      }

      const credentialsGivenToAppleForReview = "alexandre.emery+17@live.ca";
      this.showDeleteAccountRequest = activeUser.email === credentialsGivenToAppleForReview;

      try {
        this.referralPromoCode = await this.referralService.getMyReferralCode(userId);
      } catch (error) {
        logSentry(error);
        this.referralPromoCode = null;
      }

      switch (activeUser.role) {
        case Role.admin:
          this.appPages = [
            {
              title: "Administration",
              children: [
                {
                  title: "Items",
                  url: "/all-items",
                },
                {
                  title: "Orders",
                  url: "/all-orders",
                },
                {
                  title: "Customers and Staff",
                  url: "/all-users",
                },
                {
                  title: "QR Codes",
                  url: `/${adminPages.manageRacks}`,
                },
                {
                  title: "Notifications",
                  url: `/${manageNotificationsPage}`,
                },
                {
                  title: "Kiosk Mode",
                  url: `/${adminPages.manageKioskMode}`,
                },
                {
                  title: "Order fees",
                  url: `/${adminPages.orderFeesPage}`,
                },
                {
                  title: "App Versions",
                  url: `/${adminPages.manageAppVersions}`,
                },
              ],
            },
          ];
          break;

        case Role.driver:
          this.appPages = [
            {
              title: "Management",
              children: [
                {
                  title: "Orders",
                  url: "/all-orders",
                },
              ],
            },
          ];
          break;

        default:
          break;
      }
    } finally {
      this.isCheckingMenu = false;
    }
  }

  public async logout(): Promise<void> {
    await this.authService.logout(() => location.reload());
  }

  public async requestDeleteAccountRequiredForAppleReviewProcess(): Promise<void> {
    await this.messageService.presentAlert(
      "Are you sure?",
      "",
      "The request to delete your account will be processed shortly.",
      async () => {
        void this.apiService.sendPostRequest(apiPath.userDeletionRequest, { userId: this.activeUser?.id });
        await this.authService.logout();
        await this.navigationService.goToLandingPage();
      },
    );
  }

  private async storeReferralCoupon(): Promise<void> {
    try {
      const url = new URL(window.location.href);
      const value = url.searchParams.get(referralQueryParamKey);

      if (value) {
        localStorage.setItem(referralLocalStorageKey, value);
      }
    } catch {
      // localStorage can throw in private mode / when quota is exceeded; never let it block boot
    }
  }

  public readonly userToString = userToString;
  public readonly tryParseDate = tryParseDate;
  protected readonly environment = environment;
}
